import { useCallback } from 'react'
import { useModal } from '@/components/Modal/useModal'
import { useLink } from '@/hooks/useLink'
import type { QueryParams } from '@/hooks/useQuery'
import { MemoCreation } from '@/views/MemoCreation'

/**
 * Opens the memo creation dialog, and once it is done what the dialog closed
 * on - the created PM in the editor, or the planning it was filed in for a
 * template that opens that instead. Shared by the "New" menu and the main menu.
 */
export const useOpenMemoCreation = (): (query?: QueryParams) => void => {
  const { showModal, hideModal } = useModal()
  const openEditor = useLink('Editor')
  const openPlanning = useLink('Planning')

  return useCallback((query?: QueryParams) => {
    showModal(
      <MemoCreation
        id={crypto.randomUUID()}
        query={query}
        onClose={(created) => {
          hideModal()

          if (!created) {
            return
          }

          const open = created.view === 'Planning' ? openPlanning : openEditor
          open(undefined, { id: created.id }, undefined)
        }}
      />,
      'dialog',
      undefined,
      undefined,
      // The memo form holds up to four candidate columns and a preview,
      // which do not fit the default dialog width. The cap is what a wide
      // screen hits rather than the viewport, so it is what decides how much
      // room each of them gets.
      { className: 'max-w-[min(94vw,1800px)]' }
    )
  }, [showModal, hideModal, openEditor, openPlanning])
}
